import React, {FormEvent} from 'react';
import HomeModalShell from '../../home/components/HomeModalShell';
import {SafetyReportReason} from '../../../../services/safetyApi';
import styles from '../Profile.module.css';

interface ProfileSafetyActionsModalProps {
  isOpen: boolean;
  targetName: string;
  reportReason: SafetyReportReason;
  reportDetails: string;
  blocking: boolean;
  reporting: boolean;
  error: string | null;
  success: string | null;
  onReportReasonChange: (reason: SafetyReportReason) => void;
  onReportDetailsChange: (details: string) => void;
  onBlockUser: () => void;
  onClose: () => void;
  onReportSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const REPORT_REASON_OPTIONS: Array<{value: SafetyReportReason; label: string}> = [
  {value: 'harassment', label: 'Harassment'},
  {value: 'spam', label: 'Spam'},
  {value: 'inappropriate', label: 'Inappropriate content or gestures'},
  {value: 'other', label: 'Other'},
];

const ProfileSafetyActionsModal: React.FC<ProfileSafetyActionsModalProps> = ({
  isOpen,
  targetName,
  reportReason,
  reportDetails,
  blocking,
  reporting,
  error,
  success,
  onReportReasonChange,
  onReportDetailsChange,
  onBlockUser,
  onClose,
  onReportSubmit,
}) => {
  if (!isOpen) {
    return null;
  }

  const actionInProgress = blocking || reporting;

  return (
    <HomeModalShell
      backdropClassName={styles.editorModalBackdrop}
      panelClassName={`${styles.socialModalCard} ${styles.safetyModalCard}`}
      ariaLabelledBy="profile-safety-actions-title"
      ariaDescribedBy="profile-safety-actions-description"
      onClose={onClose}
      closeDisabled={actionInProgress}
    >
      <div className={styles.editorModalHeader}>
        <div>
          <h2 id="profile-safety-actions-title" className={styles.editorModalTitle}>
            Safety Actions
          </h2>
          <p id="profile-safety-actions-description" className={styles.editorModalDescription}>
            Block or report {targetName}.
          </p>
        </div>
        <button
          type="button"
          className={styles.editorModalClose}
          onClick={onClose}
          disabled={actionInProgress}
          aria-label="Close safety actions"
        >
          x
        </button>
      </div>

      <div className={styles.safetyActionPanel}>
        <div>
          <h3 className={styles.safetySubheading}>Block User</h3>
          <p className={styles.editorModalDescription}>
            Blocking removes follow relationships and prevents direct messages or comments between you.
          </p>
        </div>
        <button
          type="button"
          className={`${styles.editorModalButton} ${styles.safetyDangerButton}`}
          onClick={onBlockUser}
          disabled={actionInProgress}
        >
          {blocking ? 'Blocking...' : 'Block User'}
        </button>
      </div>

      <form onSubmit={onReportSubmit} className={styles.editorModalForm}>
        <h3 className={styles.safetySubheading}>Report User</h3>

        <label className={styles.editorField}>
          <span className={styles.editorFieldLabel}>Reason</span>
          <select
            className={styles.editorInput}
            value={reportReason}
            onChange={(event) => onReportReasonChange(event.target.value as SafetyReportReason)}
            disabled={actionInProgress}
          >
            {REPORT_REASON_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.editorField}>
          <span className={styles.editorFieldLabel}>Details</span>
          <textarea
            className={`${styles.editorInput} ${styles.safetyTextarea}`}
            value={reportDetails}
            onChange={(event) => onReportDetailsChange(event.target.value)}
            maxLength={1000}
            disabled={actionInProgress}
          />
        </label>

        {error ? (
          <div className={`${styles.editorMessage} ${styles.editorMessageError}`}>
            {error}
          </div>
        ) : null}

        {success ? (
          <div className={`${styles.editorMessage} ${styles.editorMessageSuccess}`}>
            {success}
          </div>
        ) : null}

        <div className={styles.editorModalActions}>
          <button
            type="button"
            className={`${styles.editorModalButton} ${styles.editorModalButtonSecondary}`}
            onClick={onClose}
            disabled={actionInProgress}
          >
            Close
          </button>
          <button
            type="submit"
            className={`${styles.editorModalButton} ${styles.editorModalButtonPrimary}`}
            disabled={actionInProgress}
          >
            {reporting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </form>
    </HomeModalShell>
  );
};

export default React.memo(ProfileSafetyActionsModal);
